import React from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Button } from '@aws-amplify/ui-react';
import { useViewMode } from 'contexts/ViewModeContext';

const PDFExport = ({ contentRef, filename = 'overview.pdf', onStart, onEnd }) => {
    const { isTransitioning } = useViewMode();

    const handleExport = async () => {
        // Prevent PDF export during view mode transitions to avoid Leaflet errors
        if (isTransitioning) {
            console.warn('PDF export skipped: view mode transition in progress');
            alert('Please wait for the view mode transition to complete before exporting.');
            return;
        }

        const element = contentRef.current;
        if (!element) {
            console.error('No content reference found');
            return;
        }

        try {
            // Call onStart callback if provided
            if (onStart) {
                onStart();
            }
            // Wait for any images to load
            const images = element.getElementsByTagName('img');
            await Promise.all(
                Array.from(images).map(
                    (img) =>
                        new Promise((resolve) => {
                            if (img.complete) {
                                resolve();
                            } else {
                                img.onload = resolve;
                                img.onerror = resolve;
                            }
                        })
                )
            );

            // Before cloning, capture map screenshots for PDF export
            const mapScreenshots = [];
            const originalMapContainers = element.querySelectorAll('.leaflet-container');

            for (let i = 0; i < originalMapContainers.length; i++) {
                const mapContainer = originalMapContainers[i];

                // Safety check: Skip if container is not properly initialized or during transitions
                if (!mapContainer || !mapContainer.offsetWidth || !mapContainer.offsetHeight) {
                    console.warn('Skipping invalid map container during PDF export');
                    continue;
                }

                // Additional safety check for Leaflet state during view mode transitions
                const leafletMap = mapContainer._leaflet_map;
                if (!leafletMap || leafletMap._container !== mapContainer) {
                    console.warn('Skipping map container with invalid Leaflet state during PDF export');
                    continue;
                }

                try {
                    // Capture the entire map container as an image at higher resolution
                    const mapCanvas = await html2canvas(mapContainer, {
                        scale: 2, // Higher resolution capture
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: '#f0f0f0',
                        width: mapContainer.offsetWidth,
                        height: mapContainer.offsetHeight,
                        windowWidth: window.innerWidth,
                        windowHeight: window.innerHeight,
                        scrollX: 0,
                        scrollY: 0
                    });

                    // Crop the image to center 800px if it's wider than that
                    let finalCanvas = mapCanvas;
                    let finalWidth = mapContainer.offsetWidth;
                    let finalHeight = mapContainer.offsetHeight;

                    // Account for the 2x scale in our crop calculations
                    const targetCropWidth = 800 * 2; // 1600px at 2x scale

                    if (mapCanvas.width > targetCropWidth) {
                        // Create a new canvas for the cropped image
                        const croppedCanvas = document.createElement('canvas');
                        const ctx = croppedCanvas.getContext('2d');

                        // Set the cropped canvas size (at 2x scale)
                        croppedCanvas.width = targetCropWidth;
                        croppedCanvas.height = mapCanvas.height;

                        // Calculate the crop position (center the crop)
                        const cropX = (mapCanvas.width - targetCropWidth) / 2;

                        // Draw the cropped portion onto the new canvas
                        ctx.drawImage(
                            mapCanvas,
                            cropX, 0, targetCropWidth, mapCanvas.height, // source rectangle (crop from center)
                            0, 0, targetCropWidth, mapCanvas.height      // destination rectangle
                        );

                        finalCanvas = croppedCanvas;
                        finalWidth = 800; // Display width (actual canvas is 2x this)
                        finalHeight = mapContainer.offsetHeight;
                    }

                    mapScreenshots.push({
                        dataUrl: finalCanvas.toDataURL('image/png'),
                        width: finalWidth,
                        height: finalHeight
                    });
                } catch (error) {
                    console.error('Failed to capture map screenshot:', error);
                    mapScreenshots.push(null);
                }
            }

            // Clone the overview grid
            const clone = element.cloneNode(true);
            // Remove the BuildingsSummary card from the clone (by order-9 wrapper)
            const buildingsSummaryWrapper = clone.querySelector('div.order-9');
            if (buildingsSummaryWrapper) {
                buildingsSummaryWrapper.parentNode.removeChild(buildingsSummaryWrapper);
            }

            // Create a temporary container for the clone and add it to the document
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            tempContainer.style.width = `${1100}px`;
            tempContainer.style.backgroundColor = 'white';
            tempContainer.style.padding = '20px';
            tempContainer.appendChild(clone);
            document.body.appendChild(tempContainer);

            // Wait for a short delay to ensure rendering
            await new Promise(resolve => setTimeout(resolve, 500));

            // Find all card elements using the custom class
            const cardsInit = Array.from(clone.querySelectorAll('.pdf-export-card'));

            // PDF setup
            const pdf = new jsPDF('p', 'in', 'letter');
            const pdfMargin = 0.2; // inches - further reduced margins for more space
            const pdfPageWidth = 8.5 - pdfMargin * 2; // 8.1in usable width
            const pdfPageHeight = 11 - pdfMargin * 2; // 10.6in usable height
            const captureWidthPx = 1100; // px
            const pxPerIn = captureWidthPx / pdfPageWidth;
            const pageHeightPx = pdfPageHeight * pxPerIn;

            // Calculate page breaks based on new two-column layout
            // Group widgets by type: full-width first, then half-width
            let pages = [];
            let currentPageCards = [];
            let currentPageHeight = 0;

            // Account for DealTitle height on first page
            const dealTitleWrapper = clone.querySelector('.pdf-export-header');
            let dealTitleHeight = 0;
            if (dealTitleWrapper) {
                const rect = dealTitleWrapper.getBoundingClientRect();
                dealTitleHeight = rect.height + 40; // Add some margin
            }

            // Separate full-width and half-width widgets
            const fullWidthCards = [];
            const halfWidthCards = [];

            for (let i = 0; i < cardsInit.length; i++) {
                const card = cardsInit[i];
                const shouldBeHalfWidth = card.classList.contains('pdf-export-half-width');
                const shouldBeFullWidth = card.classList.contains('pdf-export-full-width');

                if (shouldBeFullWidth || !shouldBeHalfWidth) {
                    fullWidthCards.push(i);
                } else {
                    halfWidthCards.push(i);
                }
            }


            // Estimate card heights for the new layout
            const estimateCardHeight = (card) => {
                const rect = card.getBoundingClientRect();
                const originalHeight = rect.height;
                const shouldBeHalfWidth = card.classList.contains('pdf-export-half-width');

                if (shouldBeHalfWidth) {
                    // Half-width cards - they become narrower but don't compress much in height
                    // Actually, they might become slightly taller due to text wrapping
                    return originalHeight * 1.1 + 40; // Allow for potential height increase + generous margin
                } else {
                    // Full-width cards - also be more compact
                    return originalHeight * 0.9 + 25; // Reduced margin
                }
            };

            // First, add all full-width cards to pages
            for (let i = 0; i < fullWidthCards.length; i++) {
                const cardIdx = fullWidthCards[i];
                const card = cardsInit[cardIdx];
                const estimatedHeight = estimateCardHeight(card);

                // Check if this is the first page and we need to account for DealTitle
                const titleHeightForThisPage = (currentPageCards.length === 0 && pages.length === 0) ? dealTitleHeight : 0;
                const availableHeight = pageHeightPx - titleHeightForThisPage;

                // Be more generous with space - allow more widgets per page
                const spaceBuffer = 50; // Very reduced buffer to fit more widgets

                // Check if adding this card would exceed page height
                if (currentPageCards.length > 0 && (currentPageHeight + estimatedHeight + spaceBuffer) > availableHeight) {
                    // Start a new page
                    pages.push([...currentPageCards]);
                    currentPageCards = [cardIdx];
                    currentPageHeight = estimatedHeight;
                } else {
                    // Add to current page
                    currentPageCards.push(cardIdx);
                    currentPageHeight += estimatedHeight;
                }
            }

            // If we have full-width cards, start half-width cards on a new page
            if (fullWidthCards.length > 0 && currentPageCards.length > 0) {
                pages.push([...currentPageCards]);
                currentPageCards = [];
                currentPageHeight = 0;
            }

            // Then add half-width cards, aiming for 6 per page (3 rows of 2)
            for (let i = 0; i < halfWidthCards.length; i++) {
                const cardIdx = halfWidthCards[i];
                const card = cardsInit[cardIdx];


                // Force new page every 6 cards to maintain consistent layout
                if (currentPageCards.length >= 6) {
                    pages.push([...currentPageCards]);
                    currentPageCards = [cardIdx];
                    currentPageHeight = 0; // Reset height tracking
                } else {
                    // Add to current page - we're prioritizing the 6-card limit over height estimation
                    currentPageCards.push(cardIdx);
                }
            }

            // Add the last page if it has cards
            if (currentPageCards.length > 0) {
                pages.push([...currentPageCards]);
            }



            // Render each page
            for (let p = 0; p < pages.length; p++) {
                if (p > 0) pdf.addPage();
                // Re-select cards using the custom class
                const cards = Array.from(clone.querySelectorAll('.pdf-export-card'));
                const dealTitleWrapper = clone.querySelector('.pdf-export-header');

                // Create a new container for this specific page
                const pageContainer = document.createElement('div');
                pageContainer.style.width = `${captureWidthPx}px`;
                pageContainer.style.backgroundColor = 'white';
                pageContainer.style.padding = '10px'; // Reduced padding to save space
                pageContainer.style.display = 'flex';
                pageContainer.style.flexDirection = 'column';
                pageContainer.style.gap = '12px'; // Reduced gap to fit more cards

                // For the first page, add the DealTitle
                if (p === 0 && dealTitleWrapper) {
                    const dealTitleClone = dealTitleWrapper.cloneNode(true);
                    // Preserve the original styling
                    dealTitleClone.style.width = '100%';
                    dealTitleClone.style.marginBottom = '20px';

                    // Process rating components in the DealTitle specifically
                    const dealTitleRatings = dealTitleClone.querySelectorAll('.amplify-rating, [class*="amplify-rating"]');
                    dealTitleRatings.forEach(ratingComponent => {
                        // Try to find the value from the component
                        let ratingValue = 0;
                        const valueAttr = ratingComponent.getAttribute('data-value') ||
                            ratingComponent.getAttribute('value') ||
                            ratingComponent.getAttribute('aria-valuenow');

                        if (valueAttr) {
                            ratingValue = parseFloat(valueAttr) || 0;
                        } else {
                            // Count filled stars
                            const filledStars = ratingComponent.querySelectorAll(
                                '[data-filled="true"], [class*="filled"], [aria-checked="true"], svg[fill="#f0b41c"], svg[fill*="yellow"]'
                            );
                            ratingValue = filledStars.length;
                        }

                        if (ratingValue > 0) {
                            // Create replacement stars container
                            const starsContainer = document.createElement('div');
                            starsContainer.style.display = 'inline-flex';
                            starsContainer.style.gap = '2px';
                            starsContainer.style.alignItems = 'center';

                            // Generate 5 stars based on rating value
                            for (let i = 1; i <= 5; i++) {
                                const starSpan = document.createElement('span');
                                starSpan.textContent = i <= Math.round(ratingValue) ? '★' : '☆';
                                starSpan.style.color = i <= Math.round(ratingValue) ? '#f0b41c' : '#d1d5db';
                                starSpan.style.fontSize = '1em';
                                starSpan.style.fontFamily = 'Arial, sans-serif';
                                starSpan.style.lineHeight = '1';
                                starsContainer.appendChild(starSpan);
                            }

                            // Replace the rating component
                            ratingComponent.parentNode.replaceChild(starsContainer, ratingComponent);

                            // Add proper spacing for rating labels
                            const nextSibling = starsContainer.nextElementSibling;
                            if (nextSibling && nextSibling.tagName === 'P') {
                                nextSibling.style.marginTop = '8px';
                                nextSibling.style.marginBottom = '4px';
                            }
                        }
                    });

                    // Add proper spacing for heading elements in DealTitle
                    const dealTitleHeadings = dealTitleClone.querySelectorAll('h1, h2, h3, h4, h5, h6');
                    dealTitleHeadings.forEach((heading, index) => {
                        // Check if the next sibling is a <p> tag and add spacing
                        const nextSibling = heading.nextElementSibling;
                        if (nextSibling && nextSibling.tagName === 'P') {
                            nextSibling.style.marginTop = '8px';
                            nextSibling.style.marginBottom = '4px';
                        }
                    });

                    // Alternative approach for DealTitle: Find all paragraphs and check their previous siblings
                    const dealTitleParagraphs = dealTitleClone.querySelectorAll('p');
                    dealTitleParagraphs.forEach((paragraph, index) => {
                        const prevSibling = paragraph.previousElementSibling;
                        if (prevSibling && /^H[1-6]$/.test(prevSibling.tagName)) {
                            paragraph.style.marginTop = '8px';
                            paragraph.style.marginBottom = '4px';
                        }
                    });

                    // Add PDF-specific spacing to DealTitle elements
                    const dealTitleH2 = dealTitleClone.querySelector('h2');
                    if (dealTitleH2) {
                        dealTitleH2.style.marginBottom = '24px'; // PDF-only spacing
                    }

                    // Add spacing to title divs in DealTitle for PDF
                    const titleDivs = dealTitleClone.querySelectorAll('div.flex.items-center');
                    titleDivs.forEach(div => {
                        div.style.marginBottom = '24px'; // PDF-only spacing
                    });

                    // Add spacing to group title paragraphs in DealTitle for PDF
                    const groupTitles = dealTitleClone.querySelectorAll('p.text-xl');
                    groupTitles.forEach(p => {
                        p.style.marginBottom = '24px'; // PDF-only spacing
                    });

                    pageContainer.appendChild(dealTitleClone);
                }

                // Add only the cards for this page
                let currentRowContainer = null;
                let cardsInCurrentRow = 0;

                pages[p].forEach((idx, cardPosition) => {
                    if (cards[idx]) {
                        const cardClone = cards[idx].cloneNode(true);

                        // Determine if this card should be full-width or half-width based on CSS class
                        const shouldBeFullWidth = cardClone.classList.contains('pdf-export-full-width');
                        const shouldBeHalfWidth = cardClone.classList.contains('pdf-export-half-width');

                        // Preserve and enhance the card styling
                        cardClone.style.display = 'block';
                        cardClone.style.backgroundColor = 'white';
                        cardClone.style.border = '1px solid #e2e8f0';
                        cardClone.style.borderRadius = '20px';
                        cardClone.style.padding = '8px 20px'; // Reduced top/bottom, expanded left/right
                        cardClone.style.boxShadow = 'none';
                        cardClone.style.position = 'relative';

                        if (shouldBeFullWidth || !shouldBeHalfWidth) {
                            // Full-width cards (explicit full-width or cards without specific width class)
                            cardClone.style.width = '100%';
                            cardClone.style.maxWidth = 'none';
                            cardClone.style.marginBottom = '20px';

                            // If we have an incomplete row, add it to the page first
                            if (currentRowContainer && cardsInCurrentRow > 0) {
                                pageContainer.appendChild(currentRowContainer);
                                currentRowContainer = null;
                                cardsInCurrentRow = 0;
                            }

                            pageContainer.appendChild(cardClone);
                        } else if (shouldBeHalfWidth) {
                            // Half-width cards
                            cardClone.style.width = 'calc(50% - 10px)';
                            cardClone.style.maxWidth = 'calc(50% - 10px)';
                            cardClone.style.marginBottom = '0';

                            // Create a new row container if needed
                            if (!currentRowContainer || cardsInCurrentRow >= 2) {
                                // If we have an existing container, add it to the page first
                                if (currentRowContainer && cardsInCurrentRow > 0) {
                                    pageContainer.appendChild(currentRowContainer);
                                }

                                currentRowContainer = document.createElement('div');
                                currentRowContainer.style.display = 'flex';
                                currentRowContainer.style.justifyContent = 'flex-start';
                                currentRowContainer.style.gap = '12px'; // Reduced gap between half-width cards
                                currentRowContainer.style.marginBottom = '12px'; // Reduced margin between rows
                                currentRowContainer.style.width = '100%';
                                cardsInCurrentRow = 0;
                            }

                            currentRowContainer.appendChild(cardClone);
                            cardsInCurrentRow++;

                            // If row is complete or this is the last card in the page, add to page
                            if (cardsInCurrentRow === 2 || cardPosition === pages[p].length - 1) {
                                pageContainer.appendChild(currentRowContainer);
                                currentRowContainer = null;
                                cardsInCurrentRow = 0;
                            }
                        }

                        // Handle AWS Amplify Rating components - replace entire component with Unicode stars
                        // Try multiple selectors to find rating components
                        const possibleSelectors = [
                            '[data-amplify-rating]',
                            '[class*="amplify-rating"]',
                            'div[class*="rating"]',
                            '[data-testid*="rating"]',
                            '.amplify-rating',
                            'fieldset',  // AWS Amplify Rating often uses fieldset
                            '[role="radiogroup"]'  // Rating components often use this role
                        ];

                        // Process rating components, starting with the most specific (main rating containers)
                        const processedElements = new Set();

                        possibleSelectors.forEach(selector => {
                            const ratingComponents = cardClone.querySelectorAll(selector);
                            ratingComponents.forEach(ratingComponent => {
                                // Skip if we already processed this element or its parent
                                if (processedElements.has(ratingComponent)) return;

                                // Skip if this element is a child of an already processed element
                                let isChildOfProcessed = false;
                                for (let processed of processedElements) {
                                    if (processed.contains && processed.contains(ratingComponent)) {
                                        isChildOfProcessed = true;
                                        break;
                                    }
                                }
                                if (isChildOfProcessed) return;

                                // Check if this looks like a rating component by looking for stars
                                const hasStars = ratingComponent.querySelectorAll('svg, [data-icon*="star"], [class*="star"]').length > 0;
                                if (!hasStars && selector !== 'fieldset' && selector !== '[role="radiogroup"]') return;

                                // Skip individual rating items if we're processing the main container
                                if (ratingComponent.className && ratingComponent.className.includes('amplify-rating__item')) {
                                    // Check if parent is already a rating component
                                    const parentRating = ratingComponent.closest('.amplify-rating, [class*="amplify-rating"]');
                                    if (parentRating && parentRating !== ratingComponent) {
                                        return; // Skip individual items, we'll process the parent
                                    }
                                }

                                // Try to find the value from the component or its parent
                                let ratingValue = 0;

                                // Look for data attributes that might contain the rating value
                                const valueAttr = ratingComponent.getAttribute('data-value') ||
                                    ratingComponent.getAttribute('value') ||
                                    ratingComponent.getAttribute('data-rating') ||
                                    ratingComponent.getAttribute('aria-valuenow');

                                if (valueAttr) {
                                    ratingValue = parseFloat(valueAttr) || 0;
                                } else {
                                    // Count filled/active stars if we can't find the value
                                    const filledStars = ratingComponent.querySelectorAll(
                                        '[data-filled="true"], [class*="filled"], [aria-checked="true"], svg[fill="#f0b41c"], svg[fill*="yellow"], [data-state="checked"]'
                                    );
                                    ratingValue = filledStars.length;

                                    // If still no value, try to parse from text content or other attributes
                                    if (ratingValue === 0) {
                                        const textContent = ratingComponent.textContent || '';
                                        const match = textContent.match(/(\d+(?:\.\d+)?)/);
                                        if (match) {
                                            ratingValue = parseFloat(match[1]);
                                        }
                                    }
                                }

                                // Only proceed if we found a reasonable rating value or confirmed stars
                                if (ratingValue > 0 || hasStars) {
                                    // Create replacement stars container
                                    const starsContainer = document.createElement('div');
                                    starsContainer.style.display = 'inline-flex';
                                    starsContainer.style.gap = '2px';
                                    starsContainer.style.alignItems = 'center';

                                    // Generate 5 stars based on rating value
                                    for (let i = 1; i <= 5; i++) {
                                        const starSpan = document.createElement('span');
                                        starSpan.textContent = i <= Math.round(ratingValue) ? '★' : '☆';
                                        starSpan.style.color = i <= Math.round(ratingValue) ? '#f0b41c' : '#d1d5db';
                                        starSpan.style.fontSize = '1em';
                                        starSpan.style.fontFamily = 'Arial, sans-serif';
                                        starSpan.style.lineHeight = '1';
                                        starsContainer.appendChild(starSpan);
                                    }

                                    // Replace the entire rating component
                                    ratingComponent.parentNode.replaceChild(starsContainer, ratingComponent);

                                    // Add proper spacing for BuildingRatings component
                                    // Check if the next sibling is a <p> tag (rating label)
                                    const nextSibling = starsContainer.nextElementSibling;
                                    if (nextSibling && nextSibling.tagName === 'P') {
                                        nextSibling.style.marginTop = '8px';
                                        nextSibling.style.marginBottom = '4px';
                                    }

                                    // Mark this element and its children as processed
                                    processedElements.add(ratingComponent);
                                    processedElements.add(starsContainer);
                                } else {
                                    // Mark as processed to avoid reprocessing
                                    processedElements.add(ratingComponent);
                                }
                            });
                        });

                        // Add proper spacing for heading elements and their following paragraphs
                        const headings = cardClone.querySelectorAll('h1, h2, h3, h4, h5, h6');
                        const cardName = cardClone.querySelector('h3, h4')?.textContent?.trim() || 'Unknown Card';
                        headings.forEach((heading, index) => {
                            // Check if the next sibling is a <p> or <span> tag and add spacing
                            const nextSibling = heading.nextElementSibling;
                            if (nextSibling && (nextSibling.tagName === 'P' || nextSibling.tagName === 'SPAN')) {
                                nextSibling.style.marginTop = '8px';
                                nextSibling.style.marginBottom = '4px';
                            }
                        });

                        // Alternative approach: Find all paragraphs and spans and check their previous siblings
                        const textElements = cardClone.querySelectorAll('p, span');
                        textElements.forEach((element, index) => {
                            const prevSibling = element.previousElementSibling;
                            if (prevSibling && /^H[1-6]$/.test(prevSibling.tagName)) {
                                element.style.marginTop = '8px';
                                element.style.marginBottom = '4px';
                            }
                        });

                        // Add specific spacing for AsanaStatusBar task elements
                        if (cardName.includes("Deal Status")) {
                            // Try multiple selectors to find task containers
                            let taskContainers = cardClone.querySelectorAll('div[style*="flex"]');

                            // If no flex containers found, try finding by class
                            if (taskContainers.length === 0) {
                                taskContainers = cardClone.querySelectorAll('.flex.flex-col.items-center');
                            }

                            // If still no containers, try a broader approach
                            if (taskContainers.length === 0) {
                                // Look for divs that contain task names (text content)
                                taskContainers = cardClone.querySelectorAll('div');
                                taskContainers = Array.from(taskContainers).filter(div => {
                                    const hasText = div.textContent && div.textContent.trim().length > 0;
                                    const hasChildren = div.children.length > 0;
                                    return hasText && hasChildren;
                                });
                            }

                            // Brute force approach: Find all divs with specific classes
                            const textDivs = cardClone.querySelectorAll('div.mb-1, div.text-xs');
                            const iconDivs = cardClone.querySelectorAll('div.flex.items-center');
                            const badgeDivs = cardClone.querySelectorAll('div.mt-1');

                            // Apply spacing directly to text, icon and badge divs
                            textDivs.forEach((div, index) => {
                                div.style.paddingBottom = '6px';
                            });

                            iconDivs.forEach((div, index) => {
                                div.style.marginTop = '12px';
                            });

                            badgeDivs.forEach((div, index) => {
                                div.style.marginTop = '12px';
                            });
                        }

                        // Add padding after titles in half-width widgets
                        if (shouldBeHalfWidth) {
                            const titles = cardClone.querySelectorAll('h3');
                            titles.forEach((title, index) => {
                                title.style.paddingBottom = '8px';
                                title.style.marginBottom = '4px';
                            });
                        }

                        // Handle map components specifically for PDF export
                        if (cardName.includes("Deal Map")) {
                            const mapContainers = cardClone.querySelectorAll('.leaflet-container');

                            mapContainers.forEach((container, mapIndex) => {
                                // Safety check for cloned container
                                if (!container) {
                                    console.warn('Skipping null map container in PDF clone');
                                    return;
                                }
                                const mapScreenshot = mapScreenshots[mapIndex];
                                if (!mapScreenshot) {
                                    // Fallback: just style the container properly
                                    container.style.position = 'relative';
                                    container.style.width = '100%';
                                    container.style.height = '400px';
                                    container.style.backgroundColor = '#f0f0f0';
                                    return;
                                }

                                // Clear the container and replace with screenshot
                                container.innerHTML = '';
                                container.style.position = 'relative';
                                container.style.width = `${mapScreenshot.width}px`;
                                container.style.height = `${mapScreenshot.height}px`;
                                container.style.overflow = 'hidden';
                                container.style.backgroundColor = '#f0f0f0';

                                // Create image element with the map screenshot
                                const mapImg = document.createElement('img');
                                mapImg.src = mapScreenshot.dataUrl;
                                mapImg.style.width = '100%';
                                mapImg.style.height = '100%';
                                mapImg.style.objectFit = 'cover';
                                mapImg.style.display = 'block';

                                container.appendChild(mapImg);
                            });
                        }

                        // Ensure all child elements are visible
                        const allChildren = cardClone.querySelectorAll('*');
                        allChildren.forEach(child => {
                            if (child.style.display === 'none') {
                                child.style.display = '';
                            }
                            child.style.color = 'black';



                            // Handle any remaining star icons specifically
                            if (child.textContent === '★' || child.innerHTML.includes('★') ||
                                (child.className && typeof child.className === 'string' && child.className.includes('star')) ||
                                (child.className && child.className.baseVal && child.className.baseVal.includes('star')) ||
                                (child.className && typeof child.className === 'string' && child.className.includes('rating'))) {
                                child.style.color = '#fbbf24'; // Yellow color for stars
                                child.style.fontSize = child.style.fontSize || '1em';
                            }

                            // Handle any remaining SVG elements - especially star SVGs
                            if (child.tagName === 'SVG') {
                                child.style.display = 'inline-block';
                                child.style.width = child.style.width || '1em';
                                child.style.height = child.style.height || '1em';

                                // Check multiple conditions to identify star SVGs
                                const svgContent = child.innerHTML.toLowerCase();
                                const isStarSvg = svgContent.includes('star') ||
                                    svgContent.includes('polygon') ||
                                    (child.className && child.className.baseVal && child.className.baseVal.includes('star')) ||
                                    child.getAttribute('data-icon') === 'star' ||
                                    child.getAttribute('fill') === '#f0b41c' ||
                                    child.getAttribute('fill') === '#fbbf24' ||
                                    // Check for common star path patterns
                                    svgContent.includes('m12 2l') || // Common star path start
                                    svgContent.includes('polygon') ||
                                    // Check parent context for rating
                                    (child.parentElement && (
                                        child.parentElement.getAttribute('role') === 'radio' ||
                                        child.parentElement.className.includes('rating') ||
                                        child.parentElement.getAttribute('aria-label')?.includes('star')
                                    ));

                                if (isStarSvg) {
                                    // Determine if this star should be filled based on context
                                    let isFilled = child.getAttribute('fill') === '#f0b41c' ||
                                        child.getAttribute('fill') === '#fbbf24' ||
                                        child.getAttribute('data-filled') === 'true' ||
                                        child.getAttribute('aria-checked') === 'true' ||
                                        (child.parentElement && child.parentElement.getAttribute('aria-checked') === 'true');

                                    // Replace SVG with Unicode star - more reliable for html2canvas
                                    const starSpan = document.createElement('span');
                                    starSpan.textContent = isFilled ? '★' : '☆';
                                    starSpan.style.color = isFilled ? '#f0b41c' : '#d1d5db';
                                    starSpan.style.fontSize = child.style.width || '1em';
                                    starSpan.style.display = 'inline-block';
                                    starSpan.style.fontFamily = 'Arial, sans-serif';
                                    starSpan.style.lineHeight = '1';
                                    child.parentNode.replaceChild(starSpan, child);
                                    return; // Skip further processing since we replaced the element
                                }

                                // For other SVGs, ensure they have proper styling
                                child.style.fill = child.style.fill || 'currentColor';
                            }

                            // Force star Unicode characters to be visible
                            if (child.textContent && (child.textContent.includes('★') || child.textContent.includes('☆'))) {
                                child.style.fontFamily = 'Arial, sans-serif';
                                child.style.color = '#fbbf24';
                            }
                        });
                    }
                });

                // Add the page container to the document temporarily
                document.body.appendChild(pageContainer);

                // Check if this page contains a map and wait longer if needed
                const mapContainer = pageContainer.querySelector('.leaflet-container');
                // Safety check: only consider it a valid map if it's properly initialized
                const hasMap = mapContainer && mapContainer.offsetWidth > 0 && mapContainer.offsetHeight > 0;
                const waitTime = hasMap ? 3000 : 1000; // Wait 3 seconds for maps, 1 second for other content

                // Wait for content to render properly
                await new Promise(resolve => setTimeout(resolve, waitTime));

                // Defensive check: ensure the container has content
                if (pageContainer.children.length === 0) {
                    document.body.removeChild(pageContainer);
                    continue;
                }

                // Render the page container to canvas
                const canvas = await html2canvas(pageContainer, {
                    scale: 2,
                    useCORS: true,
                    logging: true,
                    backgroundColor: '#ffffff',
                    width: captureWidthPx,
                    windowWidth: captureWidthPx,
                    allowTaint: true,
                    foreignObjectRendering: false,
                    removeContainer: true,
                    onclone: (clonedDoc) => {
                        const style = clonedDoc.createElement('style');
                        style.textContent = `
                            * {
                                color: black !important;
                                background-color: white !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            canvas, img, svg {
                                max-width: 100% !important;
                                height: auto !important;
                            }
                            .apexcharts-canvas, .apexcharts-svg {
                                background: white !important;
                            }
                            .apexcharts-text {
                                fill: black !important;
                            }
                            .apexcharts-grid line {
                                stroke: #e2e8f0 !important;
                            }
                            /* Star and rating specific styles */
                            [class*="star"], [class*="rating"] {
                                color: #fbbf24 !important;
                                font-family: Arial, sans-serif !important;
                            }
                            /* Force star Unicode characters to be visible */
                            *:contains("★"), *:contains("☆") {
                                color: #fbbf24 !important;
                                font-family: Arial, sans-serif !important;
                            }
                            /* SVG star icons */
                            svg[class*="star"], svg[fill*="yellow"], svg[fill*="gold"] {
                                fill: #fbbf24 !important;
                                display: inline-block !important;
                            }
                            /* Map specific styles for PDF export */
                            .leaflet-container {
                                width: 100% !important;
                                height: 400px !important;
                                position: relative !important;
                                background: white !important;
                            }
                            .leaflet-marker-icon, .leaflet-marker-shadow {
                                display: block !important;
                                visibility: visible !important;
                                opacity: 1 !important;
                                position: relative !important;
                            }
                            .leaflet-marker-pane, .leaflet-tile-pane, .leaflet-objects-pane {
                                position: relative !important;
                                display: block !important;
                            }
                            .leaflet-tile {
                                display: block !important;
                                visibility: visible !important;
                            }
                        `;
                        clonedDoc.head.appendChild(style);
                        // Force all text to be black
                        const allElements = clonedDoc.getElementsByTagName('*');
                        Array.from(allElements).forEach(el => {
                            el.style.color = 'black';
                            el.style.backgroundColor = 'white';
                        });
                    }
                });

                // Calculate image size for PDF
                const imgWidthIn = pdfPageWidth;
                const imgHeightIn = (canvas.height / canvas.width) * imgWidthIn;
                const imgData = canvas.toDataURL('image/png', 1.0);
                pdf.addImage(
                    imgData,
                    'PNG',
                    pdfMargin,
                    pdfMargin,
                    imgWidthIn,
                    imgHeightIn
                );

                // Clean up the page container
                document.body.removeChild(pageContainer);
            }

            // Clean up: remove the temporary container
            document.body.removeChild(tempContainer);
            pdf.save(filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            // Call onEnd callback if provided
            if (onEnd) {
                onEnd();
            }
        }
    };

    return (
        <Button
            onClick={handleExport}

            className="flex items-center justify-center rounded-lg p-2 transition-opacity hover:cursor-pointer hover:opacity-80">
            Download PDF
        </Button>
    );
};

export default PDFExport; 